import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router';
import type { Filterwaarde, FilterStand } from '@xxtract/tabelfilters';
import { Button } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage, isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, useServerTabelData, useStartadres, useTabelStand, type StartadresLijst, type TabelStand } from '@/components/tabel';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { useOrders } from '../hooks/useOrders';
import { useOrderSettings } from '../hooks/useOrderSettings';
import { useOrdersTabCounts } from '../hooks/useOrdersTabCounts';
import { OrdersFilterBar } from '../components/OrdersFilterBar';
import { OrdersTabs } from '../components/OrdersTabs';
import { OrdersTable } from '../components/OrdersTable';
import { DEFAULT_ORDER_STALENESS_DAYS } from '../orders.constants';
import {
  ORDER_FILTER_VELDEN,
  ORDERS_LIJST,
  ORDERS_MAX_PAGINAGROOTTE,
  ORDERS_PAGINA_SLEUTEL,
  ORDERS_PAGINAFILTER,
  ORDERS_PAGINAGROOTTE,
  ORDERS_SLEUTEL,
  paginafilterParameters,
  tabUitStand,
  type OrdersTab,
} from '../ordersLijst';
import type { OrdersListParams } from '../orders.api';

const ORDER_FILTER_TEKSTEN = { melding: 'orders.filters.geweigerd', velden: 'orders.filters.geweigerdVelden', wissen: 'orders.filters.wissen' } as const;

/** Voorvoegsel voor de testids van de geweigerd-filtermelding (`orders-filter-rejected`, `-clear`). */
const orderFilterTestId = 'orders';

/** Eén lezer voor beide standen: kolomfilters, sortering en pagina naar de tabel, de rest naar de paginafilter. */
const STARTLIJSTEN: readonly StartadresLijst[] = [
  { lijst: ORDERS_LIJST, standSleutel: ORDERS_SLEUTEL },
  { lijst: ORDERS_PAGINAFILTER, standSleutel: ORDERS_PAGINA_SLEUTEL },
];

type PaginafilterDeel = Partial<Record<'search' | 'attention' | 'stalenessDays', Filterwaarde | undefined>>;

/** Paginafilter bijwerken vanuit de actuele stand (niet de render), zodat een late zoekterm geen tabblad wist. */
function zetPaginafilter(deel: PaginafilterDeel) {
  const { standen, zetPaginafilter: zet } = useLijstStandStore.getState();
  const volgend: FilterStand = { ...standen[ORDERS_PAGINA_SLEUTEL]?.filters };
  for (const [sleutel, waarde] of Object.entries(deel)) {
    if (waarde === undefined || waarde === '' || (Array.isArray(waarde) && waarde.length === 0)) delete volgend[sleutel];
    else volgend[sleutel] = waarde;
  }
  zet(ORDERS_PAGINA_SLEUTEL, volgend);
}

/**
 * Orderlijst (spec-tabelfilters story 11): extern gepagineerd bij procesManagement (CAP-10). Centrale
 * filterbalk met enkelvoudige keuzelijsten zonder aantallen, sorteren op één bronkolom, en zoekveld,
 * tabbladen en drempel als paginafilter (CAP-13). Alles in de lijststand, niets in het adres (CAP-14).
 */
export function OrdersListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const kopRef = useRef<HTMLHeadingElement>(null);
  const [wisTeller, setWisTeller] = useState(0);
  const settingsQuery = useOrderSettings();

  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTLIJSTEN);
  const bewaardePaginafilter = useLijstStandStore((s) => s.standen[ORDERS_PAGINA_SLEUTEL]?.filters);
  const paginafilters = start[ORDERS_PAGINA_SLEUTEL]?.filters ?? bewaardePaginafilter;
  const pagina = paginafilterParameters(paginafilters);
  const stand = useTabelStand(ORDERS_LIJST, ORDERS_SLEUTEL, { start: start[ORDERS_SLEUTEL], afhankelijkVan: ORDERS_PAGINA_SLEUTEL, paginagrootte: ORDERS_PAGINAGROOTTE });
  const context = useMemo(() => ({ t: (s: string) => t(s) }), [t]);

  // Bij "aandacht" legt de BFF de volgorde op; bij zoeken zou een sortering bepalen wélke 100 orders doorzocht worden.
  const vergrendeld = Boolean(pagina.search) || Boolean(pagina.attention);
  const paginagrootte = Math.min(stand.paginagrootte ?? ORDERS_PAGINAGROOTTE, ORDERS_MAX_PAGINAGROOTTE);
  const [totaal, setTotaal] = useState<number>();
  const tabel = useServerTabelData(
    ORDERS_LIJST,
    { filters: stand.filters, sortering: vergrendeld ? [] : stand.sortering, pagina: stand.pagina, paginagrootte },
    totaal === undefined ? undefined : { totaal },
    { extern: true },
  );
  const p = tabel.parameters;
  const params: OrdersListParams = {
    gln: p.gln,
    serviceCode: p.serviceCode,
    status: p.status,
    from: p.from,
    to: p.to,
    sort: p.sort,
    page: tabel.pagina,
    pageSize: paginagrootte,
    ...pagina,
  };
  const ordersQuery = useOrders(params);
  // Een storing (degraded, totaal 0) is geen echt totaal: de bewaarde pagina blijft staan.
  const metaTotaal = ordersQuery.data?.meta?.degraded ? undefined : ordersQuery.data?.meta?.total;
  useEffect(() => {
    if (metaTotaal !== undefined) setTotaal(metaTotaal);
  }, [metaTotaal]);

  const { attentionCount, onholdCount } = useOrdersTabCounts({
    gln: p.gln,
    serviceCode: p.serviceCode,
    from: p.from,
    to: p.to,
    // Een ongeldige drempel gaat wel mee in de lijstvraag (de API weigert hem), niet als NaN in de tellingen.
    stalenessDays: Number.isFinite(pagina.stalenessDays) ? pagina.stalenessDays : undefined,
  });
  const tab = tabUitStand(paginafilters, stand.filters);
  const effectiveStalenessDays = pagina.stalenessDays ?? settingsQuery.data?.orderStalenessDays ?? DEFAULT_ORDER_STALENESS_DAYS;

  function kiesTab(volgend: OrdersTab) {
    zetPaginafilter({ attention: volgend === 'attention' ? ['true'] : undefined });
    stand.zetFilter('status', volgend === 'onhold' ? ['onhold'] : undefined);
  }

  // De aandachtsweg negeert `status`: een dienststatus kiezen zet "aandacht" uit, zodat de filter niet liegt.
  const balkStand: TabelStand = {
    ...stand,
    sortering: vergrendeld ? [] : stand.sortering,
    zetFilter: (sleutel, waarde) => {
      if (sleutel === 'status' && Array.isArray(waarde) && waarde.length > 0 && pagina.attention) zetPaginafilter({ attention: undefined });
      stand.zetFilter(sleutel, waarde);
    },
  };

  function openOrder(orderNumber: string) {
    navigate(`/orders/${orderNumber}`);
  }

  // Niet alleen op "afwezig" bewaken maar op "is het wel een lijst": een antwoord dat iets anders
  // meestuurt legde het hele scherm plat met "orders.filter is not a function" (gemeten 27-08-2026).
  const orders = useMemo(() => {
    const uitAntwoord = ordersQuery.data?.data;
    return Array.isArray(uitAntwoord) ? uitAntwoord : [];
  }, [ordersQuery.data]);
  const meta = ordersQuery.data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / paginagrootte) : 1;
  const page = tabel.pagina;

  // Een geweigerd filter laat kop, tabbladen en filterbalk staan; elke andere fout houdt de bestaande foutweergave.
  const filterRejected = ordersQuery.isError && isFilterRejected(ordersQuery.error, ORDER_FILTER_VELDEN);
  if (ordersQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={ordersQuery.error} onRetry={() => ordersQuery.refetch()} />;
  }

  /** spec-orders-geweigerd-filter CAP-1: breder dan reset — ook zoekveld, tabblad en drempel. */
  function clearFilters() {
    useLijstStandStore.getState().zetPaginafilter(ORDERS_PAGINA_SLEUTEL, {});
    stand.reset();
    stand.zetPaginagrootte(ORDERS_PAGINAGROOTTE);
    wisGeweigerd();
    setWisTeller((n) => n + 1);
  }

  return (
    <div className="space-y-5" data-testid="orders-page">
      <header className="flex items-start justify-between">
        <div>
          <h1 ref={kopRef} tabIndex={-1} className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('orders.title')}</h1>
          {/* Bij een geweigerd filter is het aantal onbekend: geen misleidende "0 orders". */}
          {filterRejected ? null : <p className="text-xs text-[var(--foreground-muted)]">{t('orders.countSubtitle', { count: total })}</p>}
        </div>
        <Link className="text-sm font-medium text-[var(--secondary)] underline" to="/orders/analytics">{t('orders.analytics.title')}</Link>
      </header>

      <Filterbalk
        context={context}
        data={tabel}
        lijst={ORDERS_LIJST}
        paginafilters={
          <>
            <OrdersTabs tab={tab} attentionCount={attentionCount} onholdCount={onholdCount} onChange={kiesTab} />
            <OrdersFilterBar
              key={wisTeller}
              search={pagina.search ?? ''}
              stalenessDays={typeof paginafilters?.stalenessDays === 'string' ? paginafilters.stalenessDays : ''}
              stalenessPlaceholder={settingsQuery.data?.orderStalenessDays ?? DEFAULT_ORDER_STALENESS_DAYS}
              onSearch={(search) => zetPaginafilter({ search })}
              onStalenessDays={(stalenessDays) => zetPaginafilter({ stalenessDays })}
            />
          </>
        }
        sorteringVergrendeld={vergrendeld}
        stand={balkStand}
      />

      <FilterRejectedMessage error={ordersQuery.error} focusNaWissen={kopRef} linkGeweigerd={geweigerd} onClear={clearFilters} rejected={filterRejected} teksten={ORDER_FILTER_TEKSTEN} baseTestId={orderFilterTestId} velden={ORDER_FILTER_VELDEN} />

      {filterRejected ? null : ordersQuery.isLoading ? (
        <div className="space-y-2" data-testid="orders-loading">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="skeleton h-10 rounded-lg" />
          ))}
        </div>
      ) : meta?.degraded ? (
        <div data-testid="orders-degraded" className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-16 text-center">
          <span className="material-symbols-outlined text-[40px] text-[var(--warning)]" aria-hidden="true">cloud_off</span>
          <p className="text-sm font-semibold text-[var(--foreground)]">{t('orders.degraded.title')}</p>
          <p className="max-w-md text-xs text-[var(--foreground-muted)]">{t('orders.degraded.description')}</p>
          <Button variant="outline" size="sm" onClick={() => ordersQuery.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : orders.length === 0 ? (
        <div data-testid="orders-empty" className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-16 text-center">
          <span className="material-symbols-outlined text-[40px] text-[var(--foreground-muted)]" aria-hidden="true">inbox</span>
          <p className="text-sm font-semibold text-[var(--foreground)]">{t('orders.empty.title')}</p>
          <p className="max-w-md text-xs text-[var(--foreground-muted)]">{t('orders.empty.filterHint')}</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--background)] shadow-[var(--card-shadow)]">
          {/* De tabel schuift in haar eigen vak, niet de pagina (375 px "Extra groot"); focusbaar zodat het vak met het toetsenbord te schuiven is. */}
          <div aria-label={t('orders.title')} className="overflow-x-auto rounded-t-xl focus-visible:outline-2! focus-visible:-outline-offset-2! focus-visible:outline-[var(--secondary)]!" role="region" tabIndex={0}>
            <OrdersTable
              orders={orders}
              stalenessDays={effectiveStalenessDays}
              locale={i18n.language}
              onOpenOrder={openOrder}
              onSorteer={stand.klikOpKop}
              sortering={stand.sortering}
              vergrendeld={vergrendeld}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm text-[var(--foreground-muted)]">
            <span>{t('orders.pagination.rangeInfo', { from: (page - 1) * paginagrootte + 1, to: (page - 1) * paginagrootte + orders.length, total })}</span>
            {totalPages > 1 && (
              <div className="ml-auto flex items-center gap-3">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => stand.zetPagina(page - 1)}>
                  {t('orders.pagination.previous')}
                </Button>
                <span>{t('orders.pagination.pageInfo', { page, totalPages })}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => stand.zetPagina(page + 1)}>
                  {t('orders.pagination.next')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
